import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import {
  Factory as FactoryIcon,
  Activity as ActivityIcon,
  BarChart3 as BarChart3Icon,
  ShieldCheck as ShieldCheckIcon,
  Zap as ZapIcon,
  Sun as SunIcon,
  Moon as MoonIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useSiteConfigStore,
  selectProductName,
  selectLogoUrl,
  selectDarkLogoUrl,
  selectApplicationIconUrl,
} from "@/stores/siteConfigStore";
import { useTheme } from "@/hooks/useTheme";
import LanguageSelector from "@/components/LanguageSelector";
import styles from "./AuthLayout.module.css";

function updateFavicon(iconUrl: string | null) {
  if (!iconUrl) return;
  let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }
  favicon.href = iconUrl;
  let appleIcon = document.querySelector(
    'link[rel="apple-touch-icon"]',
  ) as HTMLLinkElement | null;
  if (!appleIcon) {
    appleIcon = document.createElement("link");
    appleIcon.rel = "apple-touch-icon";
    document.head.appendChild(appleIcon);
  }
  appleIcon.href = iconUrl;
}

export default function AuthLayout() {
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useTheme();

  const productName = useSiteConfigStore(selectProductName);
  const logoUrl = useSiteConfigStore(selectLogoUrl);
  const darkLogoUrl = useSiteConfigStore(selectDarkLogoUrl);
  const applicationIconUrl = useSiteConfigStore(selectApplicationIconUrl);

  // Select the right logo for the active theme. Falls back gracefully.
  const currentLogoUrl = isDark && darkLogoUrl ? darkLogoUrl : logoUrl;
  const hasLogo = !!currentLogoUrl;

  // Apply favicon + page title based on site config (so the auth page itself
  // already reflects the tenant brand, not just post-login pages).
  useEffect(() => {
    if (productName) document.title = productName;
  }, [productName]);

  useEffect(() => {
    updateFavicon(applicationIconUrl ?? logoUrl);
  }, [applicationIconUrl, logoUrl]);

  return (
    <div className={styles.authLayout}>
      <div className={styles.authContainer}>
        {/* Left side — Branding (hidden on mobile) */}
        <div className={styles.authBranding}>
          <div className={styles.brandingContent}>
            <div className={styles.logo}>
              {hasLogo ? (
                <img
                  src={currentLogoUrl!}
                  alt={productName}
                  className={styles.logoImage}
                />
              ) : (
                <div className={styles.logoIcon}>
                  <FactoryIcon size={32} />
                </div>
              )}
              <h1 className={styles.logoText}>{productName}</h1>
            </div>

            <div className={styles.brandingText}>
              <h2>
                {t(
                  "auth.branding.title"
                )}
              </h2>
              <p>
                {t(
                  "auth.branding.description"
                )}
              </p>

              <div className={styles.features}>
                <div className={styles.feature}>
                  <ActivityIcon size={20} />
                  <span>
                    {t(
                      "auth.branding.featureRealtime"
                    )}
                  </span>
                </div>
                <div className={styles.feature}>
                  <BarChart3Icon size={20} />
                  <span>
                    {t("auth.branding.featureAnalytics")}
                  </span>
                </div>
                <div className={styles.feature}>
                  <ShieldCheckIcon size={20} />
                  <span>
                    {t(
                      "auth.branding.featureSecurity"
                    )}
                  </span>
                </div>
                <div className={styles.feature}>
                  <ZapIcon size={20} />
                  <span>
                    {t("auth.branding.featureAlerts")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side — Form */}
        <div className={styles.authFormSection}>
          <div className={styles.formContainer}>
            <div className={styles.themeToggle}>
              <LanguageSelector />
              <button
                type="button"
                onClick={toggleTheme}
                className={styles.themeButton}
                title={t("auth.theme.toggle")}
                aria-label={t("auth.theme.toggle")}
              >
                {isDark ? <SunIcon size={20} /> : <MoonIcon size={20} />}
              </button>
            </div>

            <Outlet />

            <div className={styles.authFooter}>
              <p>
                © 2024 {productName}.{" "}
                {t(
                  "auth.footer.allRightsReserved"
                )}
              </p>
              <div className={styles.footerLinks}>
                <a href="#" className={styles.footerLink}>
                  {t("auth.footer.privacy")}
                </a>
                <a href="#" className={styles.footerLink}>
                  {t("auth.footer.terms")}
                </a>
                <a href="#" className={styles.footerLink}>
                  {t("auth.footer.support")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
